import type { Logger } from '@vp/ubik-logging';
import type { Locale } from '../i18n/config';

type Price = {
    currency: string;
    list_price: {
        taxed: number;
        untaxed: number;
    };
};

type DesignableProduct = {
    price: Price;
    service_product: {
        mpv_id: string;
        product_key: string;
        name: string;
        pim_merchant_version: number;
    };
};

type LocaleCurrencyMap = { [key in Locale]: string };

const localeCurrencyMap: LocaleCurrencyMap = {
    'en-US': 'USD',
    'en-AU': 'AUD',
    'en-GB': 'GBP',
    'fr-FR': 'EUR',
    'fr-CA': 'CAD',
    'fr-CH': 'CHF',
    'de-DE': 'EUR',
    'en-CA': 'CAD',
    'en-NZ': 'NZD',
    'en-SG': 'SGD',
    'en-IE': 'EUR',
    'de-AT': 'EUR',
    'de-CH': 'CHF',
    'en-IN': 'INR',
    'it-IT': 'EUR',
    'nl-NL': 'EUR',
    'nl-BE': 'EUR',
    'fr-BE': 'EUR',
    'es-ES': 'EUR',
    'es-US': 'USD',
    'it-CH': 'CHF',
    'pt-PT': 'EUR',
    'nb-NO': 'NOK',
    'da-DK': 'DKK',
    'sv-SE': 'SEK',
    'fi-FI': 'EUR',
};

export const priceFromFloat = (price: number, locale: Locale): string => {
    const currency = localeCurrencyMap[locale];
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: 2,
    }).format(price);
};

export const getDesignableProductPrice = async (
    mpvId: string,
    culture: string,
    apiBaseUrl: string,
    logger: Logger,
): Promise<Price | null> => {
    // snyk:ignore=SSRF
    // Using our own API route as a proxy to avoid SSRF issues
    const apiURL = `${apiBaseUrl}/api/get-designable-product`;

    try {
        const options: RequestInit = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                culture,
                mpvId,
            }),
        };
        const response = await fetch(apiURL, options);

        if (!response.ok) {
            throw new Error(`Error: ${response.status}`);
        }

        const data: DesignableProduct = await response.json();
        return data.price;
    } catch (error) {
        logger.error(
            {
                name: `Failed to fetch designable product price from ${apiURL}`,
                message: `Error fetching mpvId: ${mpvId} for culture: ${culture}.`,
            },
            error,
        );
        return null;
    }
};

type LogoProductsPricing = {
    [key in Locale]: {
        'logo-essential': string;
        'logo-contest-bronze': string;
        'logomaker-icon-premium-start-price'?: string;
        'logomaker-icon-exclusive'?: string;
    };
};

export const logoProductsPricing: LogoProductsPricing = {
    'en-US': {
        'logo-essential': '',
        'logo-contest-bronze': '$299.00',
        'logomaker-icon-premium-start-price': '$9.00',
        'logomaker-icon-exclusive': '$99.00',
    },
    'en-AU': {
        'logo-essential': '',
        'logo-contest-bronze': '$449.00',
    },
    'en-GB': {
        'logo-essential': '£130',
        'logo-contest-bronze': '£249',
    },
    'fr-FR': {
        'logo-essential': '149 €',
        'logo-contest-bronze': '289 €',
    },
    'fr-CA': {
        'logo-essential': '199 $',
        'logo-contest-bronze': '409 $',
    },
    'fr-CH': {
        'logo-essential': '149 CHF',
        'logo-contest-bronze': '279 CHF',
    },
    'de-DE': {
        'logo-essential': '149 €',
        'logo-contest-bronze': '289 €',
    },
    'en-CA': {
        'logo-essential': '$199',
        'logo-contest-bronze': '$409',
    },
    'en-NZ': {
        // 99d does not support New Zealand: Revert to AUD
        'logo-essential': '$249 AUD',
        'logo-contest-bronze': '$449 AUD',
    },
    'en-SG': {
        'logo-essential': '$210',
        'logo-contest-bronze': '$409',
    },
    'en-IE': {
        'logo-essential': '149 €',
        'logo-contest-bronze': '289 €',
    },
    'de-AT': {
        'logo-essential': '€ 149',
        'logo-contest-bronze': '€ 289',
    },
    'de-CH': {
        'logo-essential': 'CHF 149',
        'logo-contest-bronze': 'CHF 279',
    },
    // 99d does not support India: Revert to USD
    'en-IN': {
        'logo-essential': '$97.00 USD',
        'logo-contest-bronze': '$299.00 USD',
    },
    'it-IT': {
        'logo-essential': '€ 149',
        'logo-contest-bronze': '€ 289',
    },
    'nl-NL': {
        'logo-essential': '€ 149',
        'logo-contest-bronze': '€ 289',
    },
    'nl-BE': {
        'logo-essential': '€ 149',
        'logo-contest-bronze': '€ 289',
    },
    'fr-BE': {
        'logo-essential': '€ 149',
        'logo-contest-bronze': '€ 289',
    },
    'es-ES': {
        'logo-essential': '€ 149',
        'logo-contest-bronze': '€ 289',
    },
    'es-US': {
        'logo-essential': '$97.00',
        'logo-contest-bronze': '$299.00',
        'logomaker-icon-premium-start-price': '$9.00',
        'logomaker-icon-exclusive': '$99.00',
    },
    'it-CH': {
        'logo-essential': 'CHF 149',
        'logo-contest-bronze': 'CHF 279',
    },
    'pt-PT': {
        'logo-essential': '€ 149',
        'logo-contest-bronze': '€ 289',
    },
    'nb-NO': {
        'logo-essential': 'NOK 1,550',
        'logo-contest-bronze': 'NOK 2,999',
    },
    'da-DK': {
        'logo-essential': '1.110 kr.',
        'logo-contest-bronze': '2.099 kr.',
    },
    'sv-SE': {
        // 99d does not support Sweden: Revert to USD
        'logo-essential': '$97.00 USD',
        'logo-contest-bronze': '$299.00 USD',
    },
    'fi-FI': {
        // 99d does not support Finland: Revert to USD
        'logo-essential': '$97.00 USD',
        'logo-contest-bronze': '$299.00 USD',
    },
};
